<template>
  <v-card
    v-if="leadData"
    border
  >
    <div class="pt-12 pb-2 pl-4 bg-primary">
      <span class="text-h6 text-white">
        <template v-if="leadData.firstname || leadData.lastname">
          {{ leadData.firstname }} {{ leadData.lastname }}
        </template>
        <template v-else>
          {{ leadData.email }}
        </template>
      </span>
    </div>
    <v-list class="base-info">
      <v-list-item>
        <DueDate
          :item="leadData"
          :custom-class="'base-info-due-date'"
          :size="'24'"
          class="pb-4 pt-2"
          @click="toggleDueDateCalendar"
        />
      </v-list-item>
      <DueDateCalendar
        v-if="showDueDateCalendar"
        :custom-class="'base-info-calendar'"
        :due-date="leadData.dueDate"
        @save="handleSaveDueDate($event)"
        @delete="handleDeleteDueDate()"
        @close="showDueDateCalendar = false"
      />
      <v-divider />
      <v-list-item>
        <template #prepend>
          <v-icon>mdi-phone</v-icon>
        </template>

        <v-list-item-title>
          <a
            v-if="leadData.formattedPhone"
            class="text-primary"
            :href="`tel:${leadData.formattedPhone}`"
          >
            {{ leadData.formattedPhone }}
          </a>
          <template v-else> ––– </template>
        </v-list-item-title>
        <v-list-item-subtitle>
          {{ $t('labels.phone-number') }}
        </v-list-item-subtitle>
        <phone-list :phone-numbers="leadData.phones" />
      </v-list-item>

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.VALUATION"
      >
        <v-list-item v-if="feature.config.isPhoneNumberVerificationEnabled">
          <template #prepend>
            <v-icon
              v-if="isDisplayedNumberVerified"
              color="success"
            >
              mdi-check-circle
            </v-icon>
            <v-icon
              v-else
              color="orange"
            >
              mdi-clock
            </v-icon>
          </template>

          <v-list-item-title class="break-line">
            <template v-if="isDisplayedNumberVerified">
              {{ $t('labels.phone-verified') }}
            </template>
            <template v-else>
              {{ $t('labels.phone-unverified') }}
            </template>
          </v-list-item-title>
        </v-list-item>
      </Feature>

      <v-list-item>
        <template #prepend>
          <v-icon>mdi-email</v-icon>
        </template>

        <v-list-item-title class="break-line">
          <a
            class="text-primary"
            :href="`mailto:${leadData.email}`"
            >{{ leadData.email }}</a
          >
        </v-list-item-title>
        <v-list-item-subtitle>
          {{ $t('labels.email') }}
        </v-list-item-subtitle>
      </v-list-item>

      <v-list-item>
        <template #prepend>
          <v-icon
            v-if="leadData.confirmedAt"
            color="success"
          >
            mdi-check-circle
          </v-icon>
          <v-icon
            v-else
            color="orange"
          >
            mdi-clock
          </v-icon>
        </template>

        <v-list-item-title class="break-line">
          <template v-if="leadData.confirmedAt">
            {{ $t('leads.table.labels.confirmed') }}
          </template>
          <template v-else>
            {{ $t('leads.table.labels.not-confirmed') }}
          </template>
        </v-list-item-title>
      </v-list-item>

      <v-list-item v-if="leadData.communicationNotAccepted">
        <template #prepend>
          <v-icon color="orange"> mdi-alert-circle </v-icon>
        </template>

        <v-list-item-title class="break-line">
          {{ $t('leads.table.labels.communication-not-accepted') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="
          leadData.phoneCommunicationAccepted !== null &&
          leadData.phoneCommunicationAccepted !== undefined
        "
      >
        <template #prepend>
          <v-icon
            v-if="leadData.phoneCommunicationAccepted"
            color="success"
          >
            mdi-check-circle
          </v-icon>
          <v-icon
            v-else
            color="orange"
          >
            mdi-alert-circle
          </v-icon>
        </template>

        <v-list-item-title class="break-line">
          <template v-if="leadData.phoneCommunicationAccepted">
            {{ $t('leads.table.labels.phone-communication-accepted') }}
          </template>
          <template v-else>
            {{ $t('leads.table.labels.phone-communication-not-accepted') }}
          </template>
        </v-list-item-title>
      </v-list-item>
      <Feature :feature-slug="featureNames.HOPPERMATION">
        <Channel
          v-for="channel in leadData.channels"
          :key="channel.id"
          :lead-id="leadData.id"
          :lead-activities="leadData.activities"
          :is-lead-deleted-by-crm-provider="isLeadDeletedByCrmProvider"
          :channel="channel"
        />
      </Feature>

      <v-list-item v-if="isLeadDeletedByCrmProvider">
        <template #prepend>
          <v-icon color="red">mdi-account-remove</v-icon>
        </template>
        <template #default>
          <v-list-item-title class="break-line">
            {{ $t('labels.crm.lead-deleted-by-provider', { crm: readableCrmName }) }}
          </v-list-item-title>
        </template>
      </v-list-item>

      <Feature
        v-if="hasCrmLink"
        :feature-slug="featureNames.CRM"
      >
        <v-list-item>
          <template #prepend>
            <v-icon> mdi-account-card </v-icon>
          </template>

          <v-list-item-title class="break-line">
            <template v-if="leadData.crmLink">
              <a
                :href="leadData.crmLink"
                target="_blank"
                rel="noopener noreferrer"
                >{{ $t('labels.crm.link-connected', { crm: readableCrmName }) }}</a
              >
            </template>
            <template v-else>
              {{ $t('labels.crm.link-not-connected', { crm: readableCrmName }) }}
            </template>
          </v-list-item-title>
        </v-list-item>
      </Feature>
    </v-list>

    <!-- Edit the Lead Data -->
    <Permission permission="lead:write">
      <v-dialog
        v-model="showDialog"
        transition="dialog-bottom-transition"
        max-width="600"
        :max-height="isMobile ? '75vh' : undefined"
        persistent
      >
        <template #activator="{ props }">
          <div class="justify-end mt-3 d-flex">
            <v-btn
              v-if="!isMasterView"
              class="mx-5 mb-5 primary text-white"
              size="small"
              v-bind="props"
            >
              <v-icon
                class="mr-2"
                size="small"
              >
                mdi-pencil
              </v-icon>
              {{ $t('buttons.lead.base-info.cta') }}
            </v-btn>
          </div>
        </template>
        <template v-slot:default="dialog">
          <v-form
            ref="editLeadForm"
            v-model="isFormValid"
            name="update-lead"
            @submit.prevent="submit(dialog)"
            @change="error = ''"
          >
            <v-card>
              <v-card-title class="pa-0">
                <v-toolbar
                  color="primary"
                  dark
                  flat
                >
                  <v-toolbar-title class="font-weight-bold">{{
                    $t('lead.base-info.title')
                  }}</v-toolbar-title>
                  <v-spacer />
                  <v-toolbar-items>
                    <v-btn
                      v-if="!isMasterView"
                      icon
                      @click="cancelDialog"
                    >
                      <v-icon size="large"> mdi-close </v-icon>
                    </v-btn>
                  </v-toolbar-items>
                </v-toolbar>
              </v-card-title>

              <v-card-text class="dialog-content-overflow">
                <v-container>
                  <v-row>
                    <v-col
                      cols="12"
                      sm="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="leadData.firstname"
                        :label="$t('labels.first-name')"
                        variant="outlined"
                        autofocus
                        @focus="validateEditLeadData"
                      />
                    </v-col>

                    <v-col
                      cols="12"
                      sm="12"
                      md="6"
                    >
                      <v-text-field
                        v-model="leadData.lastname"
                        :label="$t('labels.last-name')"
                        variant="outlined"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="12"
                      md="12"
                    >
                      <PhoneField
                        v-model="leadData.phone"
                        :rules="leadData.phone ? [rules.required] : []"
                        :error-message="errors.phone"
                        validate-on="blur"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      cols="12"
                      sm="12"
                      md="12"
                    >
                      <p>
                        {{ $t('lead.base-info.body-texts.0') }}
                      </p>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions
                v-if="!isMasterView"
                class="justify-end"
              >
                <v-btn
                  variant="text"
                  @click="cancelDialog"
                >
                  {{ $t('labels.cancel') }}
                </v-btn>
                <v-btn
                  variant="text"
                  color="primary"
                  type="submit"
                >
                  {{ $t('labels.save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </template>
      </v-dialog>
    </Permission>
    <!-- End Edit Lead -->
  </v-card>
</template>

<script>
import Permission from '@/components/Permission'
import PhoneField from '@/components/PhoneField.vue'
import bus, { eventNames } from '@/lib/eventBus'
import { required } from '@/lib/validation'
import countries from '@/mixins/countries'
import crmMixin from '@/mixins/crm'
import featureMixin from '@/mixins/feature'
import DueDate from '@/modules/leads/LeadList/Cards/DueDate.vue'
import DueDateCalendar from '@/modules/leads/LeadList/Cards/DueDateCalendar.vue'
import leadsDueDateMixin from '@/modules/leads/LeadList/Cards/mixins/leadsDueDateMixin'

import Channel from './Channel.vue'
import PhoneList from './PhoneList.vue'
import UPDATE_LEAD from './queries/UpdateLeadPersonalData.gql'

export default {
  components: { DueDateCalendar, DueDate, PhoneList, PhoneField, Channel, Permission },
  mixins: [featureMixin, crmMixin, countries, leadsDueDateMixin],
  props: {
    lead: {
      type: Object,
      default: null,
    },
    isMasterView: {
      type: Boolean,
      default: false,
    },
    isLeadDeletedByCrmProvider: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDueDateCalendar: false,
      leadData: { ...this.lead },
      errors: { phone: '' },
      isFormValid: false,
      showDialog: false,
      error: '',
    }
  },
  watch: {
    lead: {
      handler(newLead) {
        if (newLead && !this.showDialog) {
          this.leadData = { ...newLead }
        }
      },
      deep: true,
    },
  },
  computed: {
    isMobile() {
      return this.$vuetify.display.mdAndDown
    },
    isDisplayedNumberVerified() {
      if (this.leadData.formattedPhone) {
        for (const phone of this.leadData.phones) {
          if (phone.phone === this.leadData.formattedPhone && phone.verifiedAt) {
            return true
          }
        }
      }
      return false
    },
  },
  created() {
    this.rules = { required }
  },

  methods: {
    async handleSaveDueDate(event) {
      const result = await this.setDueDate({ lead: { ...this.leadData }, date: event })
      if (this.leadData.id === result.id) {
        this.leadData = { ...this.leadData, dueDate: result.dueDate }
      }
    },
    async handleDeleteDueDate() {
      const result = await this.deleteDueDate({ lead: { ...this.leadData } })
      if (this.leadData.id === result.id) {
        this.leadData = { ...this.leadData, dueDate: undefined }
      }
    },
    toggleDueDateCalendar() {
      if (this.isMasterView) {
        return
      }

      this.showDueDateCalendar = !this.showDueDateCalendar
    },
    validateEditLeadData() {
      this.$refs.editLeadForm.validate()
    },
    cancelDialog() {
      this.showDialog = false
      this.leadData = { ...this.lead }
      this.errors = { phone: '' }
      this.error = ''
    },
    async submit() {
      this.errors = { phone: '' }
      this.error = ''

      const { valid } = await this.$refs.editLeadForm.validate()
      if (valid) {
        const { id, firstname, lastname, phone } = this.leadData

        try {
          const { data } = await this.$apollo.mutate({
            mutation: UPDATE_LEAD,
            variables: {
              input: { id, firstname, lastname, phone, companyId: this.$auth.user.companyId },
            },
          })

          if (data.lead) {
            this.showDialog = false
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'success',
              text: this.$t('alerts.lead.base-info.success'),
            })
            this.$emit('lead-updated')
            this.$tracking.event('Lead', 'Clicked', 'Lead Edit Save')
          }
        } catch (e) {
          if (e.graphQLErrors?.[0]?.message === 'INVALID_PHONE_ERROR') {
            this.errors.phone = this.$t('alerts.lead.base-info.error-phone')
          }
          bus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'error',
            text: this.$t('alerts.lead.base-info.error'),
          })
        }
      }
    },
    getCountryByCode(code) {
      return (
        this.availableCountryItems.find(({ value }) => value.toUpperCase() === code?.toUpperCase())
          ?.text || code
      )
    },
  },
  emits: ['lead-updated'],
}
</script>

<style scoped>
.break-line {
  white-space: normal;
}

.base-info .v-list-item {
  min-height: 32px;
}

.dialog-content-overflow {
  overflow-y: auto;
}
</style>
